import type React from "react";
import { createContext, useContext, useState } from "react";
import { v4 as uuid } from "uuid";
import { Alert } from "@/components/Alert";
import { AlertsMainContainer } from "@/components/Alert/alert.styles";

interface AlertContextType {
  addAlert: (message: string, type?: "success" | "error" | "warning" | "info") => void;
}

interface AlertProviderProps {
  children: React.ReactNode;
}

export interface AlertType {
  id: string;
  message: string;
  type: "success" | "error" | "warning" | "info";
}

const AlertContext = createContext<AlertContextType>({ addAlert: () => {} });

export const AlertProvider: React.FC<AlertProviderProps> = ({ children }) => {
  const [alerts, setAlerts] = useState<AlertType[] | []>([]);

  const addAlert = (message: string, type = "info") => {
    const id = uuid();
    setAlerts((prev) => [
      ...prev,
      { id, message, type: type as "success" | "error" | "warning" | "info" },
    ]);

    setTimeout(() => removeAlert(id), 5000);
  };

  const removeAlert = (id: string) => {
    setAlerts((prev) => prev.filter((alert) => alert.id !== id));
  };

  return (
    <AlertContext.Provider value={{ addAlert }}>
      {children}
      <AlertsMainContainer>
        {alerts.map((alert) => (
          <Alert
            key={alert.id}
            type={alert.type}
            message={alert.message}
            onClose={() => removeAlert(alert.id)}
          />
        ))}
      </AlertsMainContainer>
    </AlertContext.Provider>
  );
};

export const useAlert = () => useContext(AlertContext);
